import { useEffect, useMemo, useRef, useState } from 'react';
import { fmtMoney } from '../../money';
import Icon from '../Icon';
import InfoTip from '../InfoTip';
import { buildTaskDetail } from '../../portfolio-detail';
import Expander from '../Expander';
import TaskDetail from './TaskDetail';
import MixBar from './MixBar';
import Spark from './Spark';
import { RagTrend } from './trend';
import { healthTrend } from '../../portfolio-trend';
import { NextMilestoneCell, DeliveredCell } from './milestone';
import { projectMilestones, nextMilestone } from '../../milestones';
import { withMilestoneForecasts, FORECAST_TRIALS_GLANCE } from '../../milestone-forecast';
import type { RollupTableProps, PortfolioRow, WaitingItem, TabKey, GroupBy } from './types';
import {
  BUILT_IN_LENSES,
  FALLBACK_DEFAULT,
  EMPTY_PORTFOLIO_FILTERS as EMPTY_FILTERS,
  configForId,
  configOfBuiltIn,
  isBuiltInView,
  sameConfig,
  type PortfolioDim as DimKey,
  type PortfolioFilters as Filters,
  type PortfolioSort as SortState,
  type PortfolioSortCol as SortCol,
  type PortfolioViewConfig,
} from '../../portfolio-views';

/**
 * THE PORTFOLIO ROLL-UP TABLE — every project on the tenant, one row each, for
 * the weekly meeting. A pixel-faithful build of the approved mockup
 * (dev-preview/portfolio/view-portfolio.html): the preset views, the group-by,
 * per-column sort and the filters are all real, operating on the `rows` prop in
 * local state. Every cell is derived data (src/portfolio.ts); nothing here is
 * typed by hand.
 *
 * FILTER / DISPLAY — the same bar the Backlog uses (#301), so the two screens
 * work the same way (approved 19 Sep, mock dev-preview/portfolio/filter-group-
 * redesign.html). The preset chips stay as one-click views; everything else
 * lives in two toolbar menus:
 *   · Filter — every dimension (Owner, Health, Budget, Waiting on, Slipping),
 *     each a checklist with counts; what is on reads back as removable pills.
 *   · Display — Group by (None / Owner / Health) and Sort.
 * Both menus close on a click away or Escape (the app-wide standard), and open
 * from the toolbar — never from a column header inside the table's scroll box,
 * so nothing clips. Column headers only sort, their direction shown by a real,
 * readable chevron (never a hairline mark).
 */

import { MONTHS } from '../../when';
const DAY_MS = 86_400_000;

/** A Date → "30 Aug". */
const fmtDate = (d: Date): string => `${d.getDate()} ${MONTHS[d.getMonth()]}`;


/** The wtag colour modifier — only these four kinds carry one; the rest read plain. */
function wtagMod(type: WaitingItem['type']): string {
  return type === 'person' || type === 'decision' || type === 'external' || type === 'parked' ? type : '';
}

/** The short kind word inside the wtag ("Person", "Decision", …). */
function wtagLabel(type: WaitingItem['type']): string {
  if (!type) return 'Waiting';
  return type.charAt(0).toUpperCase() + type.slice(1);
}

/** The health pill word for a grade — plain-language severity, not the RAG colour
 *  name (the pill is already that colour). The reason rides alongside it. */
const HEALTH_WORD: Record<'green' | 'amber' | 'red', string> = { green: 'On track', amber: 'At risk', red: 'Off track' };
const HEALTH_CLS: Record<'green' | 'amber' | 'red', string> = { green: 'g', amber: 'a', red: 'r' };

/** How long after go-live a service still reads "Settling in" — a fortnight,
 *  matching the overdue red band, so the two nudges share a clock (#435). */
const JUST_LIVE_DAYS = 14;

/** The throughput trend, folded to a word for the Moved cell's headline. */
const TREND_WORD: Record<'up' | 'down' | 'flat', string> = {
  up: '↑ above norm',
  down: '↓ below norm',
  flat: '→ about norm',
};

/** Open blockers on a board — the real "N blocked" count, never the project-level
 *  parked marker. */
function blockedCount(r: PortfolioRow): number {
  return r.waiting.filter((w) => (w.type as string) !== 'parked' && !!w.ref).length;
}

/** A live service is read on its FLOW, not a delivery RAG it no longer has (#435):
 *  anything blocked shows first; a board still inside its settling window reads
 *  "Settling in"; otherwise it is steady. The reason rides the quiet line beneath. */
function serviceHealth(r: PortfolioRow, now: number): { word: string; cls: string; why: string } {
  const blocked = blockedCount(r);
  if (blocked > 0) {
    const oldest = r.waiting.find((w) => (w.type as string) !== 'parked' && !!w.ref)?.days;
    return { word: `${blocked} blocked`, cls: r.blockers.rag === 'red' ? 'r' : 'a', why: oldest != null ? `oldest blocker ${oldest}d` : '' };
  }
  const at = r.deliverySnapshot?.at;
  if (at != null && now - at < JUST_LIVE_DAYS * DAY_MS) {
    const days = Math.max(0, Math.floor((now - at) / DAY_MS));
    return { word: 'Settling in', cls: 'settle', why: `live ${days} day${days === 1 ? '' : 's'} · flow still settling` };
  }
  const net = r.flow28?.net;
  return { word: 'Steady', cls: 'g', why: net != null ? `flow keeping up · net ${net > 0 ? '+' : ''}${net}` : '' };
}

/** The preset views, with the predicate each filters by. */
const TABS: { key: TabKey; label: string; count: boolean; test: (r: PortfolioRow) => boolean }[] = [
  { key: 'all', label: 'All', count: false, test: () => true },
  { key: 'attention', label: 'Needs attention', count: true, test: (r) => r.onHold || r.health === 'red' || r.health === 'amber' },
  { key: 'waiting', label: 'Waiting on someone', count: true, test: (r) => r.waiting.length > 0 },
  // Over budget = the forecast is past the baseline tolerance — amber (>5%) or red
  // (>12%). Green means on budget or under, so it never belongs here. (Matching the
  // health cell's own "· over budget", which shows for exactly these two grades.)
  { key: 'overbudget', label: 'Over budget', count: true, test: (r) => r.budget?.rag === 'amber' || r.budget?.rag === 'red' },
];

/** The columns a person can sort by, in the order the Display menu offers them. */
// The sort labels are the column HEADER words, so the Display→Sort list and the
// header a person clicks name the same thing (not "Project"/"Recent change").
const SORT_LABEL: Record<SortCol, string> = {
  work: 'Work', owner: 'Owner', health: 'Health', progress: 'Progress',
  waiting: 'Waiting on', golive: 'Next milestone', budget: 'Budget', moved: 'Moved',
};

/** The sort key for a column — numbers compare numerically, strings lexically. */
function sortKey(col: SortCol, r: PortfolioRow): number | string {
  switch (col) {
    case 'work': return r.name.toLowerCase();
    case 'owner': return r.owner.toLowerCase();
    case 'health': return r.onHold ? -1 : { green: 1, amber: 2, red: 3 }[r.health ?? 'green'];
    case 'progress': return r.progress;
    case 'waiting': return r.waiting.length ? (r.waiting[0].days ?? 0) : -1;
    case 'golive': return r.forecastDay ?? '9999-99-99';
    case 'budget': return r.variance ? r.variance.pct : -Infinity;
    case 'moved': return r.moved.length;
  }
}

// ── THE FILTER DIMENSIONS ───────────────────────────────────────────────────
// Each dimension turns a row into the value-key(s) it belongs to; a row passes a
// dimension when nothing is selected (all pass) or one of its keys is selected.
// The value labels are the same plain words the cells show, so a pill reads the
// way the row does.
const DIM_LABEL: Record<DimKey, string> = {
  owner: 'Owner', health: 'Health', budget: 'Budget', waiting: 'Waiting on', slip: 'Slipping',
};

const healthKey = (r: PortfolioRow): string => (r.onHold ? 'hold' : (r.health ?? 'green'));
const budgetKey = (r: PortfolioRow): string => {
  if (!r.budgetBase) return 'none';
  return r.budget?.rag === 'amber' || r.budget?.rag === 'red' ? 'over' : 'on';
};
const waitingKeys = (r: PortfolioRow): string[] =>
  r.waiting.length === 0 ? ['none'] : Array.from(new Set(r.waiting.map((w) => w.type || 'other')));
const slipKey = (r: PortfolioRow): string => {
  if (r.onHold) return 'paused';
  if (!r.planDay) return 'unscheduled';
  return r.slipDays > 0 ? 'slipping' : 'onplan';
};

/** The keys a row carries for a dimension (one, except Waiting-on which can wait
 *  on several kinds at once). */
function rowKeys(dim: DimKey, r: PortfolioRow): string[] {
  switch (dim) {
    case 'owner': return [r.owner];
    case 'health': return [healthKey(r)];
    case 'budget': return [budgetKey(r)];
    case 'waiting': return waitingKeys(r);
    case 'slip': return [slipKey(r)];
  }
}

/** The word shown for a value-key within a dimension. Owner keys are the name
 *  itself, so they pass straight through. */
const VALUE_LABEL: Record<Exclude<DimKey, 'owner'>, Record<string, string>> = {
  health: { green: 'On track', amber: 'At risk', red: 'Off track', hold: 'On hold' },
  budget: { over: 'Over budget', on: 'On budget', none: 'No budget set' },
  waiting: { person: 'Person', decision: 'Decision', external: 'External', parked: 'Parked', other: 'Other', none: 'Not waiting' },
  slip: { slipping: 'Slipping', onplan: 'On plan', unscheduled: 'Not scheduled', paused: 'Paused' },
};

/** The order value-keys are offered in (worst / most-actionable first where it
 *  helps). Owner is alphabetical and built from the data instead. */
const VALUE_ORDER: Record<Exclude<DimKey, 'owner'>, string[]> = {
  health: ['red', 'amber', 'green', 'hold'],
  budget: ['over', 'on', 'none'],
  waiting: ['person', 'decision', 'external', 'parked', 'other', 'none'],
  slip: ['slipping', 'onplan', 'unscheduled', 'paused'],
};

function valueLabel(dim: DimKey, key: string): string {
  if (dim === 'owner') return key;
  return VALUE_LABEL[dim][key] ?? key;
}

/** The default-view marker — a small, readable pushpin (not a colour, not tiny),
 *  in front of the pinned view's name. Matches the approved mock exactly. */
const PinMark = () => (
  <span className="pv-pin" title="Default view">
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 4h6" /><path d="M10 4l-.7 7-3.3 2.2V15h12v-1.8L14.7 11 14 4" /><path d="M12 15v5" />
    </svg>
  </span>
);

/** The little prompt chevron leading the empty-state hint. */
const HintChevron = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 6l6 6-6 6" />
  </svg>
);

export default function RollupTable({
  rows, loading, error, at, onOpen, onRetry, cardsByBoard, pulsesByBoard, phasesByBoard, milestonesByBoard, calendar, resolveName, onOpenCard, onMarkLive, onProducePack, producingPack,
  savedViews, defaultViewId, onSaveView, onUpdateView, onRenameView, onForgetView, onSetDefault,
}: RollupTableProps) {
  const [tab, setTab] = useState<TabKey>(FALLBACK_DEFAULT);
  const [groupBy, setGroupBy] = useState<GroupBy>('none');
  const [sort, setSort] = useState<SortState>(null);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  // Which toolbar menu is open, and — inside Filter — which dimension is drilled.
  const [menu, setMenu] = useState<'' | 'filter' | 'display'>('');
  const [field, setField] = useState<DimKey | ''>('');
  const [look, setLook] = useState('');

  // ── PERSONAL SAVED VIEWS ─────────────────────────────────────────────────
  // Which view is loaded (a built-in lens id or a saved view id). The config
  // above (tab/filters/groupBy/sort) is this view's setup, freely editable —
  // when it drifts from the loaded view we show the unsaved-changes controls.
  const [activeView, setActiveView] = useState<string>(defaultViewId || FALLBACK_DEFAULT);
  // The caret manage-menu: which tab it hangs off, and whether it is showing the
  // delete confirmation instead of the action list.
  const [menuView, setMenuView] = useState<string>('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  // The inline name form below the tabs: saving a new view, or renaming one.
  const [naming, setNaming] = useState<'' | 'new' | 'rename'>('');
  const [formName, setFormName] = useState('');
  // Which saved view a rename targets — held apart from the caret menu, so the
  // menu can close the moment "Rename…" is picked (it otherwise covers the form).
  const [renameId, setRenameId] = useState('');
  // Once the person touches the toolbar we stop re-seeding from the default, so
  // a late profile hydrate never yanks the view out from under them.
  const touched = useRef(false);

  const config: PortfolioViewConfig = { tab, filters, groupBy, sort };
  const applyConfig = (c: PortfolioViewConfig) => {
    setTab(c.tab); setFilters(c.filters); setGroupBy(c.groupBy); setSort(c.sort);
  };
  // Load a view into the toolbar (a clean reset, not composed on the leftover).
  const applyView = (id: string) => {
    const c = configForId(id, savedViews) ?? configOfBuiltIn(FALLBACK_DEFAULT);
    applyConfig(c);
    setActiveView(configForId(id, savedViews) ? id : FALLBACK_DEFAULT);
    setMenu(''); setField(''); setMenuView(''); setConfirmDelete(false); setNaming('');
    touched.current = true;
  };
  // Seed from the pinned default (or the fallback lens) until the person acts —
  // and re-seed if the default arrives late from the profile hydrate.
  useEffect(() => {
    if (touched.current) return;
    const c = configForId(defaultViewId, savedViews) ?? configOfBuiltIn(FALLBACK_DEFAULT);
    applyConfig(c);
    setActiveView(configForId(defaultViewId, savedViews) ? defaultViewId : FALLBACK_DEFAULT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultViewId, savedViews]);

  // The loaded view's stored config, and whether the toolbar has drifted from it.
  const activeConfig = configForId(activeView, savedViews) ?? configOfBuiltIn(FALLBACK_DEFAULT);
  const edited = !sameConfig(config, activeConfig);
  const activeIsSaved = !isBuiltInView(activeView) && savedViews.some((v) => v.id === activeView);
  // Which project rows are open, showing their cards in place. Collapsed by
  // default — the roll-up is a one-line-per-project read; the detail is on demand.
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const toggleRow = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // A MENU CLOSES ON A CLICK AWAY OR ESCAPE — the app-wide standard (Vinnie, 19
  // Sep: "clicking off the menu closes it, this should be a standard"), the same
  // dismissal the Backlog's Filter/Display use. The menu and the buttons that
  // open it are the only safe targets; a click anywhere else dismisses. mousedown
  // (not click) so selecting text in the search box and releasing outside is not
  // a dismissal. Covers the Filter/Display menus, a tab's caret manage-menu, and
  // the inline name form. Only listens while one of them is open.
  const anyPop = menu || menuView || naming;
  useEffect(() => {
    if (!anyPop) return;
    const closeAll = () => {
      setMenu(''); setField(''); setMenuView(''); setConfirmDelete(false); setNaming(''); setRenameId('');
    };
    const away = (e: MouseEvent) => {
      const hit = e.target as Element | null;
      if (!hit?.closest?.('.bf-menu, .pf-menu-btns, .pv-caret, .bf-naming, .bf-add')) closeAll();
    };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') closeAll(); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', key);
    };
  }, [anyPop]);

  // The distinct owners, from the full set, so the filter list stays stable.
  const owners = useMemo(
    () => Array.from(new Set(rows.map((r) => r.owner))).sort((a, b) => a.localeCompare(b)),
    [rows],
  );

  const snapMs = at ?? Date.now();

  // The nearest committee-grade milestone per project — the diamond the roll-up's
  // repurposed go-live column shows, and what that column now sorts by. Derived
  // from the go-live (off the row), the board's promoted phase ends and its
  // standalone milestone rows (0124).
  const nextMsByRow = useMemo(() => {
    const m = new Map<string, ReturnType<typeof nextMilestone>>();
    for (const r of rows) {
      const set = projectMilestones(r, phasesByBoard.get(r.id) ?? [], milestonesByBoard.get(r.id) ?? [], snapMs, cardsByBoard.get(r.id) ?? []);
      // Forecast the WHOLE set sequentially (cumulative scope), then take the cell's
      // milestone WITH its forecast already attached — so the roll-up cell and the
      // project-status drill read the one same prediction and can never disagree. A
      // light-trial run keeps the grid snappy; a milestone with no tie carries none and
      // the cell reads "from the plan".
      const enriched = withMilestoneForecasts(set, cardsByBoard.get(r.id) ?? [], snapMs, FORECAST_TRIALS_GLANCE, calendar);
      m.set(r.id, nextMilestone(enriched, snapMs));
    }
    return m;
  }, [rows, phasesByBoard, milestonesByBoard, cardsByBoard, calendar, snapMs]);

  // The roll-up's title + subtitle now live in the banded header panel above
  // (PortfolioMixBand, #435 Option B), so the table itself opens straight on its
  // tabs + toolbar; the error and loading states render just their own body.
  if (error) {
    return (
      <div className="pf-board" style={{ padding: '16px 14px' }}>
        <div style={{ fontSize: 13, color: 'var(--t2)', marginBottom: 12 }}>
          The portfolio could not be read. {error}
        </div>
        <button className="btn" onClick={onRetry}>Try again</button>
      </div>
    );
  }

  if (loading && rows.length === 0) {
    return (
      <div className="pf-board" style={{ padding: '16px 14px', fontSize: 13, color: 'var(--t3)' }}>Loading…</div>
    );
  }

  // ── The pipeline: preset view → the dimension filters → sort → group ──
  const viewTest = TABS.find((t) => t.key === tab)!.test;
  const inView = rows.filter(viewTest);
  const dims = Object.keys(EMPTY_FILTERS) as DimKey[];
  const passesDim = (dim: DimKey, r: PortfolioRow) =>
    filters[dim].length === 0 || rowKeys(dim, r).some((k) => filters[dim].includes(k));
  const passesAll = (r: PortfolioRow) => dims.every((d) => passesDim(d, r));
  const anyFilter = dims.some((d) => filters[d].length > 0);

  let shown = inView.filter(passesAll);
  const hiddenByFilters = inView.length - shown.length;
  if (sort) {
    const mul = sort.dir === 'asc' ? 1 : -1;
    shown = [...shown].sort((a, b) => {
      // The next-milestone column sorts by the diamond it shows (plan → forecast
      // day). A project with no milestone has nothing to sort — keep those rows at
      // the BOTTOM in both directions rather than letting them flip to the top on
      // a descending sort.
      if (sort.col === 'golive') {
        const ma = nextMsByRow.get(a.id);
        const mb = nextMsByRow.get(b.id);
        if (!!ma !== !!mb) return ma ? -1 : 1;
        if (!ma || !mb) return 0;
        const da = ma.forecastDay ?? ma.plannedDay ?? '';
        const db = mb.forecastDay ?? mb.plannedDay ?? '';
        return da.localeCompare(db) * mul;
      }
      const ka = sortKey(sort.col, a);
      const kb = sortKey(sort.col, b);
      const cmp = typeof ka === 'number' && typeof kb === 'number' ? ka - kb : String(ka).localeCompare(String(kb));
      return cmp * mul;
    });
  }

  // The lifecycle split is the always-on top band (#435): In delivery (built to a
  // go-live, read on schedule) vs In service (live and run, read on flow). Any
  // Display group-by (Owner / Health) then nests INSIDE each band. Owner groups
  // run alphabetically; Health groups worst-first.
  type Section = { key: string | null; label: string; rows: PortfolioRow[] };
  const groupsOf = (rowsIn: PortfolioRow[]): Section[] => {
    if (groupBy === 'owner') {
      return owners
        .filter((o) => rowsIn.some((r) => r.owner === o))
        .map((o) => ({ key: o, label: o, rows: rowsIn.filter((r) => r.owner === o) }));
    }
    if (groupBy === 'health') {
      return VALUE_ORDER.health
        .filter((k) => rowsIn.some((r) => healthKey(r) === k))
        .map((k) => ({ key: k, label: VALUE_LABEL.health[k], rows: rowsIn.filter((r) => healthKey(r) === k) }));
    }
    return [{ key: null, label: '', rows: rowsIn }];
  };
  type Band = { tier: 'indelivery' | 'inservice'; label: string; note: string; groups: Section[] };
  const BANDS: { tier: 'indelivery' | 'inservice'; label: string; note: string }[] = [
    { tier: 'indelivery', label: 'In delivery', note: 'boards being built to a Go-Live' },
    { tier: 'inservice', label: 'In service', note: 'boards live and being run' },
  ];
  const bands: Band[] = BANDS
    .map((b) => ({ ...b, groups: groupsOf(shown.filter((r) => r.tier === b.tier)) }))
    .filter((b) => b.groups.some((g) => g.rows.length > 0));

  const onHeader = (col: SortCol) => {
    touched.current = true;
    setSort((s) => (s && s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' }));
  };

  // The sort affordance on a header: a real, readable chevron (never a hairline
  // mark). Muted and pointing down on a sortable column at rest; solid and turned
  // to point up (ascending) or down (descending) on the column in use — Vinnie,
  // 19 Sep: the arrows that turn must clear the readable minimum, as a standard.
  const headerBtn = (col: SortCol, label: string) => {
    const active = sort?.col === col;
    const dir = active ? sort!.dir : 'down';
    return (
      <button className={`pf-hbtn${active ? ' is-sorted' : ''}`} onClick={() => onHeader(col)}>
        {label}
        <span className={`pf-sortmark dir-${active ? dir : 'idle'}`} aria-hidden="true">
          <Icon name="chevron" size={16} />
        </span>
      </button>
    );
  };
  // A sortable column announces its sort state to assistive tech — ascending /
  // descending on the active column, "none" on the rest.
  const ariaSort = (col: SortCol): 'ascending' | 'descending' | 'none' =>
    sort?.col === col ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none';

  // ── The Filter menu's choices for a dimension ──
  // Owner is built from the data (alphabetical); the others follow their fixed
  // order, shown only for values that actually occur, so no empty rows.
  const choicesFor = (dim: DimKey): { value: string; label: string }[] => {
    if (dim === 'owner') return owners.map((o) => ({ value: o, label: o }));
    const present = new Set(rows.flatMap((r) => rowKeys(dim, r)));
    return VALUE_ORDER[dim].filter((k) => present.has(k)).map((k) => ({ value: k, label: VALUE_LABEL[dim][k] }));
  };

  // The count beside a value: rows in this view that pass every OTHER dimension
  // and carry this value — what turning it on would leave (the same read the
  // Backlog gives).
  const countFor = (dim: DimKey, value: string): number =>
    inView.filter((r) => dims.every((d) => d === dim || passesDim(d, r)) && rowKeys(dim, r).includes(value)).length;

  const toggleValue = (dim: DimKey, value: string) => {
    touched.current = true;
    setFilters((prev) => {
      const on = prev[dim].includes(value);
      return { ...prev, [dim]: on ? prev[dim].filter((v) => v !== value) : [...prev[dim], value] };
    });
  };
  const clearDim = (dim: DimKey) => { touched.current = true; setFilters((prev) => ({ ...prev, [dim]: [] })); };
  const setGroup = (g: GroupBy) => { touched.current = true; setGroupBy(g); };
  const clearFilters = () => { touched.current = true; setFilters(EMPTY_FILTERS); };

  // ── The saved-view actions ──
  const currentConfig = (): PortfolioViewConfig => ({ tab, filters, groupBy, sort });
  // The count on a tab = rows the view's own setup would show (its lens' test and
  // its dimension filters). A built-in's config carries no filters, so it reads
  // exactly as the preset chip did.
  const countForConfig = (c: PortfolioViewConfig): number => {
    const test = TABS.find((t) => t.key === c.tab)!.test;
    return rows.filter(test).filter((r) => dims.every((d) => {
      const on = c.filters[d];
      return on.length === 0 || rowKeys(d, r).some((k) => on.includes(k));
    })).length;
  };
  // Opening the save form closes any open toolbar menu and caret menu, so nothing
  // overlaps it.
  const openSaveNew = () => {
    setNaming('new'); setFormName(''); setRenameId('');
    setMenu(''); setField(''); setMenuView(''); setConfirmDelete(false);
  };
  const submitName = async () => {
    const nm = formName.trim();
    if (!nm) return;
    if (naming === 'new') {
      const id = await onSaveView(nm, currentConfig());
      if (id) setActiveView(id);
    } else if (naming === 'rename' && renameId) {
      await onRenameView(renameId, nm);
    }
    setNaming(''); setFormName(''); setRenameId('');
  };
  const saveInPlace = () => { if (activeIsSaved) void onUpdateView(activeView, currentConfig()); };
  const resetToView = () => applyConfig(activeConfig);
  // "Rename…" closes the caret menu and opens the inline form; the target id is
  // remembered separately so closing the menu does not lose it.
  const beginRename = (id: string) => {
    const v = savedViews.find((s) => s.id === id);
    setRenameId(id); setNaming('rename'); setFormName(v?.name ?? '');
    setMenuView(''); setConfirmDelete(false);
  };
  const pinDefault = (id: string) => { void onSetDefault(id); setMenuView(''); setConfirmDelete(false); };
  const doDelete = (id: string) => {
    void onForgetView(id);
    setMenuView(''); setConfirmDelete(false);
    if (activeView === id) applyView(defaultViewId || FALLBACK_DEFAULT);
  };

  const renderRow = (r: PortfolioRow) => {
    const isOpen = expanded.has(r.id);
    const cards = cardsByBoard.get(r.id) ?? [];
    const sub = cards.length ? `${cards.length} ${cards.length === 1 ? 'card' : 'cards'}` : 'No cards yet';
    // A board that has been handed over to service reads on its flow, not a
    // delivery schedule (#435); one still inside its settling window wears a tag.
    const svc = r.tier === 'inservice';
    const justLive = svc && r.deliverySnapshot != null && snapMs - r.deliverySnapshot.at < JUST_LIVE_DAYS * DAY_MS;
    const row = (
      <tr key={r.id} className={`pf-row${r.onHold ? ' held' : ''}${isOpen ? ' open' : ''}`} onClick={() => onOpen(r.id)}>
      {/* Work */}
      <td>
        <div className="pf-work">
          <button
            type="button"
            className="pf-disc"
            aria-expanded={isOpen}
            aria-label={isOpen ? `Hide the cards on ${r.name}` : `Show the cards on ${r.name}`}
            onClick={(e) => { e.stopPropagation(); toggleRow(r.id); }}
          >
            <span className="pf-chev"><Icon name="chevron" size={18} /></span>
          </button>
          <div className="pf-work-main">
            <div className="pf-work-name">{r.name}{justLive && <span className="pl-justlive">Just live</span>}</div>
            <div className="pf-work-sub">{sub}</div>
            {!svc && !r.onHold && r.overdue && (
              onMarkLive ? (
                <button
                  type="button"
                  className={`pl-overdue as-btn ${r.overdue.level === 'red' ? 'is-red' : 'is-amber'}`}
                  onClick={(e) => { e.stopPropagation(); onMarkLive(r.id); }}
                  title="Open this board to Mark it Live"
                >
                  <Icon name="warning" size={13} />
                  {r.overdue.days} {r.overdue.days === 1 ? 'day' : 'days'} overdue — <b>Mark it Live</b>
                </button>
              ) : (
                <div className={`pl-overdue ${r.overdue.level === 'red' ? 'is-red' : 'is-amber'}`}>
                  <Icon name="warning" size={13} />
                  {r.overdue.days} {r.overdue.days === 1 ? 'day' : 'days'} overdue — <b>Mark it Live</b>
                </div>
              )
            )}
          </div>
        </div>
      </td>
      {/* Owner */}
      <td><div className="pf-owner">{r.owner}</div></td>
      {/* Health — the status word as a readable pill with a solid colour dot; its
          reason rides on a quiet line beneath (never crammed into the chip). */}
      <td>
        {r.onHold ? (
          <span className="rag hold"><span className="dot" />On hold</span>
        ) : svc ? (
          (() => {
            const h = serviceHealth(r, snapMs);
            return (
              <div className="rag-cell">
                <span className={`rag ${h.cls}`}><span className="dot" />{h.word}</span>
                {h.why ? <span className="rag-why">{h.why}</span> : null}
              </div>
            );
          })()
        ) : (
          <div className="rag-cell">
            <span className={`rag ${HEALTH_CLS[r.health ?? 'green']}`}>
              <span className="dot" />{HEALTH_WORD[r.health ?? 'green']}
            </span>
            {r.health !== 'green' && r.healthWhy ? <span className="rag-why">{r.healthWhy}</span> : null}
            {/* Better or worse than last week — a quiet line, the pill untouched. */}
            <RagTrend trend={healthTrend(pulsesByBoard.get(r.id) ?? [])} />
          </div>
        )}
      </td>
      {/* Progress — the delivery % for a project being built; for a live service
          (no delivery % left to run) the column reads its FLOW instead (#435). */}
      <td>
        {svc ? (
          r.flow28 ? (
            <div className="pf-pct flow">
              <span className="fig">{r.flow28.perWeek}/wk</span>
              <span className={`flow-net ${r.flow28.net < 0 ? 'good' : r.flow28.net > 0 ? 'warn' : ''}`}>
                net {r.flow28.net > 0 ? '+' : ''}{r.flow28.net}
              </span>
            </div>
          ) : (
            <div className="pf-pct flow"><span className="fig" style={{ color: 'var(--t3)', fontWeight: 500 }}>No flow yet</span></div>
          )
        ) : (
          <div className="pf-pct">
            <span className="fig">{r.projectPct}%</span>
            <div className="bar"><div className={`fill${r.onHold ? ' hold' : ''}`} style={{ width: `${r.projectPct}%` }} /></div>
            {/* The plain-words schedule read — how the work sits against the dates,
                said in words. Shown only when it carries a signal; the headline RAG
                cell stays the go-live verdict, deliberately distinct. */}
            {r.progressSchedule.kind !== 'none' && (
              <div className={`pf-sched is-${r.progressSchedule.tone === 'good' ? 'good' : r.progressSchedule.tone === 'bad' ? 'bad' : r.progressSchedule.tone === 'warn' ? 'warn' : 'neutral'}`}>
                {r.progressSchedule.label}
              </div>
            )}
            {/* The per-card %-complete read (#progress): how many of this board's
                cards carry a percent that is lagging the calendar, or that their own
                pace projects will miss the due date. A quiet sub-line, shown only
                when something is off; silent when every tracked card is on track. */}
            {r.progressCounts.behind + r.progressCounts.atRisk > 0 && (
              <div className="pf-deliv">
                {r.progressCounts.atRisk > 0 && (
                  <span className="pf-deliv-item" title={`${r.progressCounts.atRisk} card${r.progressCounts.atRisk === 1 ? '' : 's'} won't make their due date at the current pace`}>
                    <span className="pf-deliv-pip is-bad" />{r.progressCounts.atRisk} at risk
                  </span>
                )}
                {r.progressCounts.behind > 0 && (
                  <span className="pf-deliv-item" title={`${r.progressCounts.behind} card${r.progressCounts.behind === 1 ? '' : 's'} behind what the calendar expects by now`}>
                    <span className="pf-deliv-pip is-warn" />{r.progressCounts.behind} behind
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </td>
      {/* Mix — this board's Run-vs-Change split, by card count (#435). A portfolio
          summary, so it sits with the high-level status columns and reads at rest,
          rather than off the right edge past the wide schedule/budget columns. */}
      <td><MixBar mix={r.mix} /></td>
      {/* Waiting on */}
      <td>{renderWaiting(r)}</td>
      {/* Next milestone · plan → forecast — for a handed-over service row the
          column instead carries the AS-DELIVERED go-live record (#435, Vinnie
          22 Oct): the delivered twin of the upcoming go-live a delivery row shows. */}
      <td>
        {svc && r.deliverySnapshot
          ? <DeliveredCell snap={r.deliverySnapshot} />
          : <NextMilestoneCell next={nextMsByRow.get(r.id)} onHold={r.onHold} />}
      </td>
      {/* Budget → forecast */}
      <td>{renderBudget(r)}</td>
      {/* Moved — led by the headline the meeting reads first (closed this week, or
          the lead movement), the sparkline demoted to a mark beside it and the norm
          folded to a word; the other verbs drop to one quiet line. "No movement"
          when nothing moved and nothing closed in the window (never invented). */}
      <td>
        {r.throughput ? (
          <div className="mvx">
            <div className="mvx-head">
              <Spark values={r.throughput.weekly} />
              <span className="mvx-fig">{r.throughput.thisWeek} closed</span>
              <span className={`mvx-trend ${r.throughput.dir}`}>{TREND_WORD[r.throughput.dir]}</span>
            </div>
            {(() => {
              // The headline already says "N closed", so drop the closed verb here.
              const rest = r.moved.filter((v) => !/\bclosed\b/.test(v));
              return rest.length ? <div className="mvx-sub">{rest.join(' · ')}</div> : null;
            })()}
          </div>
        ) : r.moved.length ? (
          <div className="mvx">
            <div className="mvx-head"><span className="mvx-fig">{r.moved[0]}</span></div>
            {r.moved.length > 1 ? <div className="mvx-sub">{r.moved.slice(1).join(' · ')}</div> : null}
          </div>
        ) : (
          <div className="mvx quiet"><span className="mvx-fig">No movement</span></div>
        )}
      </td>
      </tr>
    );
    // The detail row is ALWAYS rendered so its panel can slide open and shut on
    // the shared Expander (mounted-but-inert while closed) rather than snapping in
    // and out of the table. The rows are projects — a bounded set — so building
    // each board's detail up front is affordable.
    const detail = buildTaskDetail(cards, { now: snapMs });
    return [
      row,
      <tr key={`${r.id}-detail`} className="pfd-row">
        <td colSpan={9}>
          <Expander open={isOpen}>
            <TaskDetail detail={detail} now={snapMs} resolveName={resolveName} boardId={r.id} onOpenCard={onOpenCard} />
          </Expander>
        </td>
      </tr>,
    ];
  };

  function renderWaiting(r: PortfolioRow) {
    if (r.waiting.length === 0) {
      return <div className="pf-wait none"><span className="wtxt">—</span></div>;
    }
    const w = r.waiting[0];
    const mod = wtagMod(w.type);
    let wageText: string | null = null;
    let wageOld = false;
    if (w.type === 'parked' && w.target) {
      wageText = `picked up: ${w.target}`;
    } else if (w.days != null) {
      wageText = `since ${fmtDate(new Date(snapMs - w.days * DAY_MS))} · ${w.days}d`;
      wageOld = w.days > 14;
    }
    return (
      <div className="pf-wait">
        <span className={`wtag${mod ? ` ${mod}` : ''}`}>{wtagLabel(w.type)}</span>
        <div className="wtxt">{w.label}</div>
        {wageText ? <div className={`wage${wageOld ? ' old' : ''}`}>{wageText}</div> : null}
        {r.waiting.length > 1 ? <span className="wmore">+{r.waiting.length - 1} more</span> : null}
      </div>
    );
  }


  function renderBudget(r: PortfolioRow) {
    const base = fmtMoney(r.budgetBase);
    if (r.onHold) {
      // Paused: show the baseline if we have one, and say it's paused — never the
      // "→ —" that reads as a missing forecast rather than a stopped clock.
      return (
        <div className="pf-budget">
          {base ? <div className="fig" style={{ color: 'var(--t3)' }}>{base}</div> : null}
          <div className="slip held">Paused</div>
        </div>
      );
    }
    // A handed-over service board shows the budget it FROZE at go-live — the
    // as-delivered figure is the money half of the delivered snapshot and wins
    // over any live budget still sitting on the row, so a service row always
    // reads the figure it went live on (#435, Vinnie 22 Oct).
    const snap = r.tier === 'inservice' ? r.deliverySnapshot : undefined;
    if (snap?.budgetAtGoLive != null) {
      return (
        <div className="pf-budget">
          <div className="fig"><span className="b-fore">{fmtMoney(snap.budgetAtGoLive)}</span></div>
          <div className="b-note">at Go-Live</div>
        </div>
      );
    }
    if (!base) {
      // No budget set yet — a plain word, not a bare dash that looks like a bug.
      return (
        <div className="pf-budget">
          <div className="fig" style={{ color: 'var(--t3)' }}>No budget set</div>
        </div>
      );
    }
    let varLine: JSX.Element | null = null;
    if (r.variance) {
      const { diff, pct } = r.variance;
      if (pct > 0) {
        varLine = <div className={`var ${pct > 12 ? 'bad' : 'warn'}`}>+{fmtMoney(diff)} · {pct.toFixed(0)}% over</div>;
      } else if (pct < 0) {
        varLine = <div className="var ok">{fmtMoney(-diff)} · under</div>;
      } else {
        varLine = <div className="var ok">on budget</div>;
      }
    }
    // Baseline known; the figure the eye lands on is the DERIVED estimate-at-completion
    // — what it will cost now, from the board's pace and the spend — the SAME number the
    // variance % and the Budget health pill read (3.31.0). The hand-typed forecast no
    // longer drives the verdict, so it must not be the arrow target: drawing "base →
    // hand-typed" beside an EAC-derived "% over" let the figure and the percentage
    // disagree (F6). Shown as base → EAC only while the EAC is ready; below the readiness
    // floor (too early to project from pace) the baseline stands alone, with no variance
    // line to contradict.
    const eac = r.evm?.eac;
    return (
      <div className="pf-budget">
        <div className="fig">
          {eac != null ? (
            <><span className="b-base">{base} → </span><span className="b-fore">{fmtMoney(eac)}</span></>
          ) : (
            <span className="b-fore">{base}</span>
          )}
        </div>
        {varLine}
      </div>
    );
  }

  const activeDims = dims.filter((d) => filters[d].length > 0);

  // The view Portfolio opens on — the pin sits on this one. A pinned id that is
  // gone falls back to the built-in lens, so the pin always has a home.
  const resolvedDefault = (configForId(defaultViewId, savedViews) ? defaultViewId : FALLBACK_DEFAULT);
  const nameOf = (id: string): string =>
    isBuiltInView(id)
      ? BUILT_IN_LENSES.find((l) => l.id === id)!.name
      : (savedViews.find((v) => v.id === id)?.name ?? '');

  // One view tab. Built-ins carry a caret only while active (its menu just pins
  // the default); saved views always carry one (shown on hover, always on active).
  const renderTab = (id: string, name: string, count: number | null, saved: boolean) => {
    const active = activeView === id;
    const caret = saved || active;
    return (
      <span className="bf-view-wrap" key={id}>
        <button
          role="tab"
          aria-selected={active}
          className={`bf-view${active ? ' bf-on' : ''}`}
          onClick={() => applyView(id)}
        >
          {resolvedDefault === id && <PinMark />}
          {name}
          {count != null && <span className="bf-view-n">{count}</span>}
          {active && edited && <span className="pv-dot" title="Unsaved changes" />}
        </button>
        {caret && (
          <button
            className={`pv-caret${menuView === id ? ' on' : ''}`}
            aria-label={`Manage ${name}`}
            aria-expanded={menuView === id}
            onClick={() => { setMenuView(menuView === id ? '' : id); setConfirmDelete(false); }}
          >
            <Icon name="chevron" size={16} />
          </button>
        )}
        {menuView === id && (
          <div className="bf-menu pv-menu" role="menu" aria-label={`Manage ${name}`}>
            {confirmDelete ? (
              <div className="pv-confirm">
                <p>Delete <b>“{name}”</b>? This can’t be undone — the built-in views and your own are untouched.</p>
                <div className="row">
                  <button className="btn btn-small btn-danger" onClick={() => doDelete(id)}>Delete view</button>
                  <button className="btn btn-small" onClick={() => setConfirmDelete(false)}>Cancel</button>
                </div>
              </div>
            ) : saved ? (
              <>
                <ul className="bf-fields">
                  <li><button onClick={() => beginRename(id)}>Rename…</button></li>
                  <li>
                    <button onClick={() => pinDefault(id)}>
                      <span className="bf-tick" aria-hidden="true">{resolvedDefault === id ? '✓' : ''}</span>Set as default
                    </button>
                  </li>
                  <li><button className="pv-del" onClick={() => setConfirmDelete(true)}>Delete view</button></li>
                </ul>
                <p className="pv-menu-note">Personal to you. The four built-in views can’t be renamed or deleted — only set as your default.</p>
              </>
            ) : (
              <>
                <ul className="bf-fields">
                  <li>
                    <button onClick={() => pinDefault(id)}>
                      <span className="bf-tick" aria-hidden="true">{resolvedDefault === id ? '✓' : ''}</span>Set as default
                    </button>
                  </li>
                </ul>
                <p className="pv-menu-note">A built-in view. Pin it as the one Portfolio opens on; it can’t be renamed or deleted.</p>
              </>
            )}
          </div>
        )}
      </span>
    );
  };

  // The "remembers…" caption under the save form — the whole setup, in words.
  const caption = (
    <>
      Remembers the <b>{TABS.find((t) => t.key === tab)!.label}</b> tab
      {activeDims.map((d) => (
        <span key={d}>, <b>{DIM_LABEL[d]}: {filters[d].map((v) => valueLabel(d, v)).join(' or ')}</b></span>
      ))}
      {groupBy !== 'none' && <>, grouped by <b>{groupBy === 'owner' ? 'Owner' : 'Health'}</b></>}
      {sort && <>, sorted by <b>{SORT_LABEL[sort.col]}</b></>}
      . Personal to you — it won’t change what anyone else sees.
    </>
  );

  return (
    <>
      <div className="bf-views pf-views" role="tablist" aria-label="Views">
        {BUILT_IN_LENSES.map((l) =>
          renderTab(l.id, l.name, l.counted ? countForConfig(configOfBuiltIn(l.id)) : null, false),
        )}
        {savedViews.map((v) => renderTab(v.id, v.name, countForConfig(v), true))}

        <button className="bf-add" aria-label="Save this view" title="Save this view" onClick={openSaveNew}>+</button>
        {edited && (
          <span className="pv-dirty">
            {activeIsSaved && <button className="btn btn-small btn-primary" onClick={saveInPlace}>Save</button>}
            <button className="link pv-reset" onClick={resetToView}>{activeIsSaved ? 'Reset' : 'Reset changes'}</button>
          </span>
        )}

        {/* Filter / Display — the Backlog's bar, opening from the toolbar so the
            menus never sit inside the table's scroll box. */}
        <div className="pf-menu-btns">
          <button
            className={`btn btn-small${menu === 'filter' ? ' bf-open' : ''}`}
            aria-expanded={menu === 'filter'}
            onClick={() => { setMenu(menu === 'filter' ? '' : 'filter'); setField(''); }}
          >
            Filter
            {anyFilter && <span className="bf-n">{activeDims.length}</span>}
          </button>
          <button
            className={`btn btn-small${menu === 'display' ? ' bf-open' : ''}`}
            aria-expanded={menu === 'display'}
            onClick={() => setMenu(menu === 'display' ? '' : 'display')}
          >
            Display
          </button>

          {/* Produce the Monthly Steering Pack — the whole portfolio as a branded
              A4 PDF, downloaded straight to the browser. Available to anyone who
              can see the Portfolio; disabled while there is nothing to produce. */}
          {onProducePack && (
            <button
              className="btn btn-small"
              data-reveal="produce-pack"
              disabled={!!producingPack || rows.length === 0}
              onClick={onProducePack}
              title="Download the whole portfolio as a branded steering-committee PDF"
            >
              {producingPack ? 'Producing…' : 'Portfolio Report (PDF)'}
            </button>
          )}

          {/* FILTER: the dimension first, then its values. */}
          {menu === 'filter' && (
            <div className="bf-menu" role="dialog" aria-label="Filter">
              {!field ? (
                <>
                  <p className="bf-menu-head">Filter by…</p>
                  <ul className="bf-fields">
                    {dims.map((d) => (
                      <li key={d}>
                        <button onClick={() => { setField(d); setLook(''); }}>
                          {DIM_LABEL[d]}
                          {filters[d].length > 0 && <span className="bf-n">{filters[d].length}</span>}
                          <span className="bf-field-chev" aria-hidden="true"><Icon name="chevron" size={16} /></span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <>
                  <p className="bf-menu-head">
                    <button className="bf-back" onClick={() => setField('')}>
                      <Icon name="chevronLeft" size={16} /> Back
                    </button>
                    {DIM_LABEL[field]}
                  </p>
                  {field === 'owner' && (
                    <input
                      className="bf-look"
                      value={look}
                      placeholder="Search owner…"
                      aria-label="Search owner"
                      onChange={(e) => setLook(e.target.value)}
                    />
                  )}
                  <ul className="bf-values">
                    {choicesFor(field)
                      .filter((c) => c.label.toLowerCase().includes(look.trim().toLowerCase()))
                      .map((c) => (
                        <li key={c.value}>
                          <button
                            aria-pressed={filters[field].includes(c.value)}
                            onClick={() => toggleValue(field, c.value)}
                          >
                            <span className="bf-tick" aria-hidden="true">
                              {filters[field].includes(c.value) ? '✓' : ''}
                            </span>
                            {c.label}
                            <span className="bf-n">{countFor(field, c.value)}</span>
                          </button>
                        </li>
                      ))}
                  </ul>
                </>
              )}
            </div>
          )}

          {/* DISPLAY: group and sort — nothing here hides a project. */}
          {menu === 'display' && (
            <div className="bf-menu bf-display" role="dialog" aria-label="Display">
              <p className="bf-menu-head">Group by</p>
              <ul className="bf-values">
                {(['none', 'owner', 'health'] as GroupBy[]).map((g) => (
                  <li key={g}>
                    <button aria-pressed={groupBy === g} onClick={() => setGroup(g)}>
                      <span className="bf-tick" aria-hidden="true">{groupBy === g ? '✓' : ''}</span>
                      {g === 'none' ? 'None' : g[0].toUpperCase() + g.slice(1)}
                    </button>
                  </li>
                ))}
              </ul>
              <p className="bf-menu-head">Sort</p>
              <ul className="bf-values">
                {(Object.keys(SORT_LABEL) as SortCol[]).map((c) => (
                  <li key={c}>
                    <button aria-pressed={sort?.col === c} onClick={() => onHeader(c)}>
                      <span className="bf-tick" aria-hidden="true">{sort?.col === c ? '✓' : ''}</span>
                      {SORT_LABEL[c]}
                      {sort?.col === c && (
                        <span className={`pf-sortmark dir-${sort.dir}`} aria-hidden="true"><Icon name="chevron" size={16} /></span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
              <p className="bf-safe">Nothing here hides a project.</p>
            </div>
          )}
        </div>
      </div>

      {/* The name form — saving the current setup as a new view, or renaming one.
          A built-in can't be overwritten, so + is always save-as-new. */}
      {naming && (
        <>
          {naming === 'rename' && <div className="pv-form-h">Rename view</div>}
          <form className="bf-naming" onSubmit={(e) => { e.preventDefault(); void submitName(); }}>
            <input
              className="input"
              autoFocus
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Name this view"
              aria-label={naming === 'rename' ? 'Rename this view' : 'Name this view'}
            />
            <button className="btn btn-small btn-primary" type="submit">{naming === 'rename' ? 'Save name' : 'Save view'}</button>
            <button className="btn btn-small" type="button" onClick={() => { setNaming(''); setFormName(''); setRenameId(''); }}>Cancel</button>
          </form>
          {naming === 'new' && <p className="pv-cap">{caption}</p>}
        </>
      )}

      {/* First run: no saved views yet. The built-ins carry the screen; one quiet
          line teaches the + gesture, then gets out of the way once a view exists. */}
      {savedViews.length === 0 && !naming && (
        <p className="pv-hint">
          <HintChevron />
          <span>
            Set the portfolio the way you want it — the tab, filters, grouping and sort — then press <b>+</b> to
            save it as a view only you see. <b>{nameOf(resolvedDefault)}</b> is your default for now.
          </span>
        </p>
      )}

      {/* THE PILLS: what is on, in words, each removable. */}
      {anyFilter && (
        <div className="bf-pills pf-pills">
          {activeDims.map((d) => (
            <span key={d} className="bf-pill">
              <span className="bf-pill-k">{DIM_LABEL[d]}</span>
              <span className="bf-pill-v">{filters[d].map((v) => valueLabel(d, v)).join(' or ')}</span>
              <button
                className="bf-pill-x"
                aria-label={`Stop filtering by ${DIM_LABEL[d].toLowerCase()}`}
                onClick={() => clearDim(d)}
              >
                ×
              </button>
            </span>
          ))}
          <button className="link bf-clear" onClick={clearFilters}>
            Clear filters
          </button>
        </div>
      )}

      <div className="pf-board">
        <table className="pf-table">
          <thead>
            <tr>
              <th aria-sort={ariaSort('work')}>{headerBtn('work', 'Work')}</th>
              <th aria-sort={ariaSort('owner')}>{headerBtn('owner', 'Owner')}</th>
              <th aria-sort={ariaSort('health')}>{headerBtn('health', 'Health')}</th>
              <th aria-sort={ariaSort('progress')}>{headerBtn('progress', 'Progress')}</th>
              {/* Run vs Change (#435) — a scan column, not a sort: the split is a
                  shape to read, not an ordering. Exact counts on hover. The InfoTip
                  explains the coined word in place, for anyone who scrolls past the
                  band above it. Sits by the status columns so it reads at rest. */}
              <th className="pf-mix-h">
                <span className="pf-mix-h-in">
                Mix
                <InfoTip label="Mix — Run vs Change">
                  This board’s <b>Change-vs-Run</b> split, by card count — how much is building
                  something new (<b>Change</b>) versus keeping the service running (<b>Run</b>). The
                  bar shows the split; hover it for the exact counts.
                  <br />
                  <br />
                  Example: a “patch file servers” card is Run; a “new VPN gateway” is Change.
                </InfoTip>
                </span>
              </th>
              <th aria-sort={ariaSort('waiting')}>{headerBtn('waiting', 'Waiting on')}</th>
              <th aria-sort={ariaSort('golive')}>{headerBtn('golive', 'Next milestone')}</th>
              <th className="num" aria-sort={ariaSort('budget')}>{headerBtn('budget', 'Budget → forecast')}</th>
              <th aria-sort={ariaSort('moved')}>{headerBtn('moved', 'Moved · last 7 days')}</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 ? (
              <tr><td colSpan={9}><div className="pf-owner" style={{ color: 'var(--t3)' }}>No projects match this view.</div></td></tr>
            ) : (
              bands.flatMap((band) => [
                <tr key={`band-${band.tier}`} className="pf-grouprow pf-bandrow">
                  <td colSpan={9}>
                    <div className="pf-group">
                      <span className="pl-band"><b>{band.label}</b> <span className="pl-band-note">· {band.note}</span></span>
                    </div>
                  </td>
                </tr>,
                ...band.groups.flatMap((sec) => [
                  ...(sec.key !== null
                    ? [<tr key={`grp-${band.tier}-${sec.key}`} className="pf-grouprow"><td colSpan={9}><div className="pf-group pf-subgroup">{sec.label} <span className="pf-group-n">{sec.rows.length}</span></div></td></tr>]
                    : []),
                  ...sec.rows.flatMap(renderRow),
                ]),
              ])
            )}
          </tbody>
        </table>
      </div>

      {/* WHAT IS HIDDEN, AND WHY — said out loud, so a filter never quietly drops
          a project a manager was looking for. */}
      {hiddenByFilters > 0 && (
        <p className="bf-said pf-said">
          {shown.length} shown · {hiddenByFilters} hidden by these filters
        </p>
      )}

      <div className="pf-legend">
        <h4>How Health is worked out — the worst of three, each read from the project itself</h4>
        <div className="pf-legend-grid">
          <div className="pf-sig">
            <div className="st">Schedule
              <span className="dots">
                <span className="d" style={{ background: 'var(--s-done)' }} />
                <span className="d" style={{ background: 'var(--s-inprogress)' }} />
                <span className="d" style={{ background: 'var(--p-critical)' }} />
              </span>
            </div>
            <div className="sd">Go-Live vs today, plus any phase now overdue.</div>
          </div>
          <div className="pf-sig">
            <div className="st">Budget
              <span className="dots">
                <span className="d" style={{ background: 'var(--s-done)' }} />
                <span className="d" style={{ background: 'var(--s-inprogress)' }} />
                <span className="d" style={{ background: 'var(--p-critical)' }} />
              </span>
            </div>
            <div className="sd">Forecast vs baseline — within ±5% green, over 5% amber, over 12% red.</div>
          </div>
          <div className="pf-sig">
            <div className="st">Blockers
              <span className="dots">
                <span className="d" style={{ background: 'var(--s-done)' }} />
                <span className="d" style={{ background: 'var(--s-inprogress)' }} />
                <span className="d" style={{ background: 'var(--p-critical)' }} />
              </span>
            </div>
            <div className="sd">Oldest open blocker's age — none green, over 1 week amber, over 2 weeks red.</div>
          </div>
        </div>
        <div className="pf-legend-foot">On hold is its own state, not a colour — a parked project is hushed from the at-risk view but never shows green. Nothing is typed by hand.</div>
      </div>
    </>
  );
}
